import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapPin, Phone, Check } from "lucide-react";
import { toast } from "sonner";
import { api, euro, img, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Field, Page, Spinner } from "@/components/Layout";

const Galleria = ({ immagini, nome }) => {
  const [sel, setSel] = useState(0);
  const cur = immagini[sel];
  return (
    <div data-testid="vetrina-gallery">
      <div className="aspect-[3/2] rounded-3xl overflow-hidden border border-[#E3D9F0] bg-[#EEE7F6]">
        {cur && <img src={img(cur.path_1600)} alt={`${nome} ${sel + 1}`} className="w-full h-full object-cover" data-testid="gallery-main" />}
      </div>
      <div className="grid grid-cols-5 gap-3 mt-3">
        {immagini.map((im, i) => (
          <button key={im.slot} onClick={() => setSel(i)} data-testid={`gallery-thumb-${im.slot}`}
            className={`aspect-[3/2] rounded-xl overflow-hidden border-2 transition-colors ${i === sel ? "border-[#7E5BAF]" : "border-transparent hover:border-[#E3D9F0]"}`}>
            <img src={img(im.path_400)} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

const Acquisto = ({ op }) => {
  const p = op.vetrina.prodotto;
  const { user } = useAuth();
  const nav = useNavigate();
  const [importo, setImporto] = useState(p.tagli_cent?.[0] || null);
  const [libero, setLibero] = useState("");
  const [f, setF] = useState({ quantita: 1, regalo: false, destinatario_nome: "", destinatario_email: "", messaggio: "", data_consegna: "",
    acquirente_nome: user?.nome || "", acquirente_email: user?.email || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const importoCent = libero ? Math.round(parseFloat(libero.replace(",", ".")) * 100) : importo;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, operatore_id: op.id, importo_cent: importoCent, quantita: Number(f.quantita),
        destinatario_email: f.regalo ? f.destinatario_email : null, data_consegna: f.regalo && f.data_consegna ? f.data_consegna : null };
      const { data } = await api.post("/pubblico/ordini", body);
      nav(`/checkout/${data.ordine_id}?t=${data.token}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card space-y-5 lg:sticky lg:top-24" data-testid="purchase-form">
      <div>
        <p className="caption">Scegli l'importo</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(p.tagli_cent || []).map((t) => (
            <button type="button" key={t} data-testid={`taglio-${t}`} onClick={() => { setImporto(t); setLibero(""); }}
              className={`px-4 py-2 rounded-xl border font-medium transition-colors ${importo === t && !libero ? "bg-[#7E5BAF] text-white border-[#7E5BAF]" : "border-[#E3D9F0] hover:border-[#7E5BAF]"}`}>
              {euro(t)}
            </button>
          ))}
        </div>
        {p.importo_libero && (
          <div className="mt-3">
            <input className="field" inputMode="decimal" placeholder={`Importo libero (${euro(p.importo_min_cent)} – ${euro(p.importo_max_cent)})`}
              value={libero} onChange={(e) => setLibero(e.target.value)} data-testid="importo-libero-input" />
          </div>
        )}
      </div>
      <Field label="Quantità">
        <input type="number" min={1} max={20} className="field" value={f.quantita} onChange={set("quantita")} data-testid="quantita-input" />
      </Field>
      <div className="grid grid-cols-2 gap-2 p-1 bg-[#EEE7F6] rounded-xl">
        {[[false, "Per me"], [true, "È un regalo"]].map(([v, l]) => (
          <button type="button" key={l} onClick={() => setF({ ...f, regalo: v })} data-testid={`regalo-toggle-${v ? "si" : "no"}`}
            className={`py-2 rounded-lg text-sm font-medium transition-colors ${f.regalo === v ? "bg-[#FFFEFF] shadow-sm" : "text-[#786E65]"}`}>{l}</button>
        ))}
      </div>
      {f.regalo && (
        <div className="space-y-4" data-testid="regalo-fields">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Nome destinatario"><input required className="field" value={f.destinatario_nome} onChange={set("destinatario_nome")} data-testid="dest-nome-input" /></Field>
            <Field label="Email destinatario"><input required type="email" className="field" value={f.destinatario_email} onChange={set("destinatario_email")} data-testid="dest-email-input" /></Field>
          </div>
          <Field label={`Messaggio (${f.messaggio.length}/300)`}>
            <textarea maxLength={300} rows={3} className="field" value={f.messaggio} onChange={set("messaggio")} data-testid="messaggio-input" />
          </Field>
          <Field label="Data di consegna" hint="Lascia vuoto per inviarla subito. Le consegne programmate partono al mattino del giorno scelto.">
            <input type="date" className="field" min={new Date().toISOString().slice(0, 10)} value={f.data_consegna} onChange={set("data_consegna")} data-testid="data-consegna-input" />
          </Field>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Il tuo nome"><input required className="field" value={f.acquirente_nome} onChange={set("acquirente_nome")} data-testid="acq-nome-input" /></Field>
        <Field label="La tua email"><input required type="email" className="field" value={f.acquirente_email} onChange={set("acquirente_email")} data-testid="acq-email-input" /></Field>
      </div>
      <div className="flex items-center justify-between border-t border-[#E3D9F0] pt-4">
        <span className="text-[#786E65]">Totale</span>
        <span className="font-display text-3xl" data-testid="totale">{importoCent ? euro(importoCent * Number(f.quantita || 0)) : "—"}</span>
      </div>
      <button className="btn-primary w-full" disabled={busy || !importoCent} data-testid="vai-al-pagamento-btn">{busy ? "Attendere…" : "Vai al pagamento"}</button>
      <p className="text-xs text-[#786E65] text-center">Non serve un account. Pagamento con carta, Apple Pay o PayPal.</p>
    </form>
  );
};

export default function Vetrina() {
  const { id } = useParams();
  const [op, setOp] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/pubblico/vetrina/${id}`).then((r) => setOp(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);
  if (err) return <Page><div className="card text-center py-16" data-testid="vetrina-error">{err}</div></Page>;
  if (!op) return <Spinner />;
  const p = op.vetrina.prodotto;
  return (
    <Page>
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-10">
          <Galleria immagini={op.vetrina.immagini} nome={op.ragione_sociale} />
          <div>
            <p className="caption">{op.categoria} · {op.citta}</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2" data-testid="vetrina-nome">{op.ragione_sociale}</h1>
            <h2 className="text-base md:text-lg text-[#7E5BAF] mt-2" data-testid="vetrina-titolo">{p.titolo}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-[#786E65] mt-4">
              <span className="flex items-center gap-1"><MapPin size={14} />{op.indirizzo}, {op.citta} ({op.provincia})</span>
              <span className="flex items-center gap-1"><Phone size={14} />{op.telefono}</span>
            </div>
          </div>
          <div className="rich text-[#3d322c] leading-relaxed" dangerouslySetInnerHTML={{ __html: p.descrizione_html }} data-testid="vetrina-descrizione" />
          <div className="grid sm:grid-cols-2 gap-4">
            {[["Validità", `${p.validita_mesi} mesi dall'acquisto`], ["Tipo di buono", p.tipo_buono === "multiuso" ? "Multiuso" : "Monouso"],
              ["Utilizzo", p.frazionabile ? "Frazionabile in più spese" : "Da usare in un'unica soluzione"], ["Dove riscattare", p.dove_riscattare]].map(([k, v]) => (
              <div key={k} className="border border-[#E3D9F0] rounded-xl p-4 bg-[#FFFEFF]">
                <p className="caption">{k}</p>
                <p className="mt-1 flex gap-2"><Check size={16} className="text-[#7E5BAF] mt-1 shrink-0" />{v}</p>
              </div>
            ))}
          </div>
          {p.condizioni && (
            <div><p className="caption">Condizioni d'uso</p><p className="mt-2 whitespace-pre-line text-sm text-[#786E65]">{p.condizioni}</p></div>
          )}
        </div>
        <div className="lg:col-span-5"><Acquisto op={op} /></div>
      </div>
    </Page>
  );
}
